import { useEffect, useRef, useState } from "react";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { Color, FontFamily, FontSize, TextStyle } from "@tiptap/extension-text-style";
import Highlight from "@tiptap/extension-highlight";
import { FilePlus2 } from "lucide-react";
import { cleanPaste } from "@/lib/richtext";
import { RichToolbar } from "@/components/RichToolbar";

const WIKI_OPEN = /\[\[([^[\]|]*)$/;

// Typing [[ opens a page finder at the caret
const useWikiSuggest = (pages) => {
  const [sug, setSug] = useState(null);
  const ref = useRef({ sug: null, idx: 0, pick: null });
  const matches = sug ? pages.filter((p) => p.title.toLowerCase().includes(sug.q.toLowerCase())).slice(0, 8) : [];
  const exact = sug && pages.some((p) => p.title.toLowerCase() === sug.q.trim().toLowerCase());
  const options = [...matches.map((p) => p.title), ...(sug?.q.trim() && !exact ? [sug.q.trim()] : [])];
  ref.current.sug = sug;
  ref.current.options = options;
  const check = (editor) => {
    const { $from, empty } = editor.state.selection;
    const m = empty && $from.parent.textBetween(Math.max(0, $from.parentOffset - 80), $from.parentOffset, undefined, "\ufffc").match(WIKI_OPEN);
    if (!m) return setSug(null);
    const at = editor.view.coordsAtPos($from.pos);
    setSug((s) => ({ q: m[1], from: $from.pos - m[0].length, to: $from.pos, x: at.left, y: at.bottom + 6, idx: s?.q === m[1] ? s.idx : 0 }));
  };
  return { sug, setSug, options, matches: matches.length, check, ref };
};

const Suggest = ({ s, options, matches, pick }) => (
  <div className="fixed z-50 w-64 rounded-lg border border-amber-500/30 bg-panel shadow-xl p-1" style={{ left: Math.min(s.x, window.innerWidth - 270), top: s.y }} data-testid="rte-wiki-suggest">
    {options.length === 0 && <p className="text-xs text-slate-500 px-2 py-1.5">Type a page name…</p>}
    {options.map((t, i) => (
      <button key={t} type="button" onMouseDown={(e) => { e.preventDefault(); pick(t); }} data-testid={`rte-wiki-option-${i}`}
        className={`w-full flex items-center gap-1.5 text-left text-sm px-2 py-1.5 rounded truncate ${i === s.idx ? "bg-amber-500/15 text-amber-200" : "text-slate-300"}`}>
        {i >= matches && <FilePlus2 className="w-3.5 h-3.5 text-rose-300 shrink-0" />}{i >= matches ? `Link to new page “${t}”` : t}
      </button>
    ))}
  </div>
);

export const RichEditor = ({ value, onChange, pages, toolbarExtra, onReady, compact, testId }) => {
  const w = useWikiSuggest(pages);
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3] }, link: { openOnClick: false } }),
      TextStyle, Color, FontFamily, FontSize, Highlight.configure({ multicolor: true }),
    ],
    content: value,
    onUpdate: ({ editor: e }) => { onChange(e.isEmpty ? "" : e.getHTML()); w.check(e); },
    onSelectionUpdate: ({ editor: e }) => w.check(e),
    editorProps: {
      attributes: { class: `wiki-prose ${compact ? "min-h-[7rem]" : "min-h-[22rem]"} px-4 py-3 focus:outline-none`, "data-testid": testId || "page-content-input" },
      transformPastedHTML: cleanPaste,
      handleKeyDown: (_, ev) => {
        const { sug, options } = w.ref.current;
        if (!sug) return false;
        if (ev.key === "ArrowDown" || ev.key === "ArrowUp") {
          const d = ev.key === "ArrowDown" ? 1 : -1;
          w.setSug((s) => ({ ...s, idx: (s.idx + d + Math.max(options.length, 1)) % Math.max(options.length, 1) }));
          return true;
        }
        if ((ev.key === "Enter" || ev.key === "Tab") && options.length) { w.ref.current.pick(options[sug.idx] ?? options[0]); return true; }
        if (ev.key === "Escape") { w.setSug(null); return true; }
        return false;
      },
    },
  });
  w.ref.current.pick = (title) => {
    const s = w.ref.current.sug;
    editor.chain().focus().insertContentAt({ from: s.from, to: s.to }, `[[${title}]] `).run();
    w.setSug(null);
  };
  useEffect(() => { if (editor) onReady?.(editor); }, [editor]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!editor) return null;
  return (
    <div className="rounded-xl border border-line bg-canvas focus-within:border-amber-500/40 transition-colors" data-testid="rich-editor">
      <RichToolbar editor={editor} extra={toolbarExtra} />
      <EditorContent editor={editor} />
      {w.sug && <Suggest s={w.sug} options={w.options} matches={w.matches} pick={w.ref.current.pick} />}
    </div>
  );
};
